/**
 * Resizable horizontal module layout.
 *
 * Shared desktop split for Reading, Listening, and Writing module panels.
 *
 * @module ResizableModulePanels
 */

import type { ReactNode } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { cn } from '@/lib/utils';

export interface ResizableModulePanelsProps {
  readonly autoSaveId: string;
  readonly left: ReactNode;
  readonly right: ReactNode;
  readonly leftDefaultSize: number;
  readonly rightDefaultSize: number;
  readonly leftMinSize: number;
  readonly rightMinSize: number;
  readonly resizeLabel?: string;
}

export function ResizableModulePanels({
  autoSaveId,
  left,
  right,
  leftDefaultSize,
  rightDefaultSize,
  leftMinSize,
  rightMinSize,
  resizeLabel = 'Drag to resize the module panels.',
}: ResizableModulePanelsProps) {
  return (
    <PanelGroup
      autoSaveId={autoSaveId}
      direction="horizontal"
      className="flex h-full min-h-0 flex-1"
    >
      <Panel
        defaultSize={leftDefaultSize}
        minSize={leftMinSize}
        className="min-h-0 min-w-0"
      >
        <div className="h-full min-h-0 overflow-hidden">{left}</div>
      </Panel>

      <PanelResizeHandle
        className={cn(
          'group relative flex h-full w-2.5 shrink-0 items-center justify-center',
          'border-x border-border bg-muted/40 outline-none transition-colors',
          'hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          'cursor-col-resize',
        )}
        aria-orientation="vertical"
        aria-label={resizeLabel}
      >
        <span className="pointer-events-none inline-flex rounded-full bg-border/80 px-0.5 py-2 opacity-70 group-hover:opacity-100">
          <span aria-hidden className="block h-4 w-0.5 rounded-full bg-muted-foreground/60" />
        </span>
      </PanelResizeHandle>

      <Panel
        defaultSize={rightDefaultSize}
        minSize={rightMinSize}
        className="min-h-0 min-w-0"
      >
        <div className="h-full min-h-0 overflow-hidden">{right}</div>
      </Panel>
    </PanelGroup>
  );
}
